import React, { useState } from 'react';
import { RSButton, RSCheckbox } from 'reactsymbols-kit';
import MerlinSelect, { type MerlinSelectOption } from '../MerlinSelect';
import { useAddInheritanceInfo, useAsynchronousAction, useController } from '../../hooks';
import AddInheritanceController from '../../merode/controllers/AddInheritanceController';
import Styles from '../../styles/colors';
import { produce } from 'immer';
import type { IModelVersion as IControllerModelVersion } from '../../merode/controllers/Controller';
import type { IModelVersion } from '../../stores/projects/model_version';
import type { IModel } from '../../stores/model';
import type { IMethod } from '../../stores/model/metamodel/method';
import type Report from '../../merode/checks/Report';
import { asRSChange, rsValue, type RSCheckboxChange } from '../Inspector/rsForm';

/** A typed controller method, re-typed to the hook's loose action shape. */
type AsyncActionLike = (...args: unknown[]) => Promise<unknown>;

/** Map of supertype-method id -> chosen specialisation action. */
type SpecialisationChoiceMap = Record<string, string>;

const SPECIALISATION_NONE = 'NONE';
const SPECIALISATION_CREATE = 'CREATE';

interface SpecialisationSelectProps {
  /** The id of the currently selected method or NONE or CREATE. */
  value: string;
  /** Function to set the value. */
  onChange: (value: string) => void;
  /** List of candidate methods. */
  candidateMethods: IMethod[];
}

/**
 * A dropdown to select the method/event to use for specialisation of the given supertype
 * method/event.
 *
 * Props:
 * - value: the id of the currently selected method or NONE or CREATE
 * - onChange: function to set the value
 * - candidateMethods: list of candidate methods
 */
const SpecialisationSelect = ({ value, onChange, candidateMethods }: SpecialisationSelectProps) => {
  const options = [
    {
      label: `Don't create a specialised event`,
      value: SPECIALISATION_NONE,
    },
    {
      label: 'Create a new specialised event',
      value: SPECIALISATION_CREATE,
    },
  ].concat(
    candidateMethods.map((m) => ({
      label: `Use subtype event ${m.ownerEventType.name} [${m.displayStringWithMetadata}]`,
      value: m.id,
    }))
  );
  return (
    <MerlinSelect
      className="rs-input-wrap"
      options={options}
      value={value}
      onChange={(option: MerlinSelectOption | null) =>
        option != null && onChange(option.value as string)
      }
      searchable={false}
      clearable={false}
    />
  );
};

/**
 * Form to add an inheritance.
 *
 * Props:
 * - modelVersion: the ModelVersion to add the inheritance to.
 * - supertype: the id of the supertype for the new inheritance.
 * - subtype: the id of the subtype for the new inheritance.
 * - onCancel: callback to cancel adding a new inheritance.
 * - onReport: callback to send the report up.
 */
interface AddInheritanceFormProps {
  /** The ModelVersion to add the inheritance to. */
  modelVersion: IModelVersion;
  /** The id of the supertype for the new inheritance. */
  supertype: string;
  /** The id of the subtype for the new inheritance. */
  subtype: string;
  /** Callback to cancel adding a new inheritance. */
  onCancel?: () => void;
  /** Callback to send the report up. */
  onReport?: (report: Report) => void;
}

/**
 * Form to add an inheritance.
 *
 * Props:
 * - modelVersion: the ModelVersion to add the inheritance to.
 * - supertype: the id of the supertype for the new inheritance.
 * - subtype: the id of the subtype for the new inheritance.
 * - onCancel: callback to cancel adding a new inheritance.
 * - onReport: callback to send the report up.
 */
const AddInheritanceForm = ({ modelVersion, supertype, subtype, onCancel, onReport }: AddInheritanceFormProps) => {
  // Store `IModelVersion` is the same loaded node the controller's narrower
  // `IModelVersion` describes; cast at the construction boundary only.
  const controller = useController(
    () => new AddInheritanceController(modelVersion as unknown as IControllerModelVersion)
  );
  const addInheritanceAction = useAsynchronousAction(
    controller.addInheritance as AsyncActionLike,
    (report) => {
      if (onReport != null) {
        onReport(report as Report);
      }
    }
  );
  // `content` is the loaded model here (non-null); the hook wants the `IModel`
  // interface and `string` ids, matching the original JS.
  const { abstract: defaultAbstract, specialisations } = useAddInheritanceInfo(
    modelVersion.content as unknown as IModel,
    supertype,
    subtype
  );

  const [abstract, setAbstract] = useState(defaultAbstract);
  const [specialisationChoices, setSpecialisationChoices] = useState<SpecialisationChoiceMap>(() => {
    const defaultValues: SpecialisationChoiceMap = {};
    for (const s of specialisations) {
      defaultValues[s.superMethod.id] = s.defaultValue;
    }
    return defaultValues;
  });

  const busy = addInheritanceAction.busy;
  const valid = true; // There isn't anything to check?
  const addInheritance = () => {
    addInheritanceAction.execute(supertype, subtype, abstract, specialisationChoices);
  };

  return (
    <div className="merlin-modal add-inheritance-form">
      <div className="title">Add inheritance relation</div>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !busy) {
            addInheritance();
          }
        }}
      >
        <div className="modal-body">
          <div className="container">
            <div className="row">
              <div className="col">
                <RSCheckbox
                  name="abstract"
                  label="Supertype is abstract"
                  checked={abstract}
                  onChange={asRSChange<RSCheckboxChange>(
                    (value: string | number, checked: boolean) => setAbstract(checked)
                  )}
                  disabled={busy}
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                Please select how you would like to specialise the events in the supertype.
              </div>
            </div>
            <div className="row">
              <div className="col">
                {specialisations.map((s, i) => {
                  const m = s.superMethod;
                  return (
                    <div
                      key={m.id}
                      className="row specialisation"
                      style={{ position: 'relative', zIndex: 100 - i }}
                    >
                      <div className="col supertype-event">
                        <label>Supertype event</label>
                        <span className="event-type-name">{m.ownerEventType.name}</span>
                        <span className="event-metadata">[{m.displayStringWithMetadata}]</span>
                      </div>
                      <div className="col">
                        <SpecialisationSelect
                          value={specialisationChoices[m.id]}
                          onChange={(value) =>
                            setSpecialisationChoices(
                              produce((draft: SpecialisationChoiceMap) => {
                                draft[m.id] = value;
                              })
                            )
                          }
                          candidateMethods={s.candidateMethods}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="button-wrapper">
          <RSButton
            value="Cancel"
            type="button"
            background={Styles.defaultGradient}
            color={Styles.darkTextColor}
            onClick={onCancel}
          />
          <RSButton
            type="submit"
            value={rsValue(busy ? null : 'Add')}
            background={Styles.blueGradient}
            disabled={!valid || busy}
            iconName={rsValue(busy ? 'FaSpinner' : null)}
            iconSize={15}
            className={rsValue(busy ? 'spin' : null)}
          />
        </div>
      </form>
    </div>
  );
};

export default AddInheritanceForm;
